import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GoogleMap,
    InfoWindow,
    Marker,
    HeatmapLayer,
    Polyline,
    Polygon,
    OverlayView,
    OverlayViewF
} from '@react-google-maps/api';
import SendIcon from '@mui/icons-material/Send';
import { Box, Button, Modal, Stack, Typography, useMediaQuery, Drawer, IconButton, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { MapLegendContext } from './MapLegendContext';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NavigationIcon from '@mui/icons-material/Navigation';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useEffectOnce } from 'react-use';
import { addRoute, selectRouteById } from '../../../store/runSlice';
import { debounce, generateRandomColor, getPolygonCentroid } from '../../../utils';
import SendImageDialog from '../SendImageDialog';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import GpsSpeedOverlay from './Overlays/GpsSpeedOverlay';
import GpsQualityOverlay from './Overlays/GpsQualityOverlay';
import GpsSatellitesOverlay from './Overlays/GpsSatellitesOverlay';
import WeedItPressureOverlay from './Overlays/WeedItPressureOverlay';
import WeedItPwmModeOverlay from './Overlays/WeedItPwmModeOverlay';
import WeedItStatusOverlay from './Overlays/WeedItStatusOverlay';
import WeedItSensitivityOverlay from './Overlays/WeedItSensitivityOverlay';
import WeedItMarginOverlay from './Overlays/WeedItMarginOverlay';
import WeedItSpeedOverlay from './Overlays/WeedItSpeedOverlay';
import FlowRateOverlay from './Overlays/FlowRateOverlay';
import ApplicationRateOverlay from './Overlays/ApplicationRateOverlay';
import ActiveBoomOverlay from './Overlays/ActiveBoomOverlay';
import LteRSRPOverlay from './Overlays/LteRSRPOverlay';
import LteRSRQOverlay from './Overlays/LteRSRQOverlay';
import RouteOverlay from './Overlays/RouteOverlay';
import PlaybackController from './PlaybackController';

const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100%',
    zIndex: 50,
    borderRadius: '25px'
};

const MarkerAI = ({
    runId,
    handleActiveMarker,
    setActiveMarker,
    activeMarker,
    baseUrl,
    marker: { id, name, position, images, eventDateUtc }
}) => {
    const [isAiImageModalOpen, setIsAiImageModalOpen] = useState(false);
    const [isSendImageDialogOpen, setIsSendImageDialogOpen] = useState(false);

    const openAiImageModal = () => setIsAiImageModalOpen(true);
    const closeAiImageModal = () => setIsAiImageModalOpen(false);
    const openSendImageDialog = () => setIsSendImageDialogOpen(true);
    const closeSendImageDialog = () => setIsSendImageDialogOpen(false);

    return (
        <Marker
            key={id}
            position={position}
            onClick={() => handleActiveMarker(id)}
        >
            {activeMarker === id ? (
                <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                    <Box>
                        {images.map((aiImage, index) => (
                            <Box key={index} style={{ textAlign: 'center' }}>
                                <img
                                    src={baseUrl + aiImage.image}
                                    style={{
                                        width: '350px',
                                        height: 'auto',
                                        paddingBottom: '4px'
                                    }}
                                    onClick={openAiImageModal}
                                />
                                <Typography style={{ py: '4px' }}>
                                    {aiImage.aiDiscoveries?.join(', ')}
                                </Typography>
                                <Typography sx={{ py: '4px' }}>
                                    {position.lat + ', ' + position.lng}
                                </Typography>
                                <Stack justifyContent="flex-end">
                                    <Button
                                        variant="outlined"
                                        startIcon={<SendIcon />}
                                        onClick={openSendImageDialog}
                                    >
                                        Send
                                    </Button>
                                </Stack>
                                {isSendImageDialogOpen && (
                                    <SendImageDialog
                                        imageCapture={aiImage}
                                        open={isSendImageDialogOpen}
                                        onClose={closeSendImageDialog}
                                        onSuccess={closeSendImageDialog}
                                    />
                                )}
                                <Modal
                                    open={isAiImageModalOpen}
                                    onClose={closeAiImageModal}
                                    isBlocking={false}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: '100%',
                                            maxWidth: 'calc(100% - 32px)',
                                            borderRadius: '20px',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img
                                            src={baseUrl + aiImage.image}
                                            style={{ width: '100%', height: 'auto' }}
                                            onClick={closeAiImageModal}
                                        />
                                    </Box>
                                </Modal>
                            </Box>
                        ))}
                    </Box>
                </InfoWindow>
            ) : null}
        </Marker>
    );
};

const ENDPOINT_MARKER =
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

const overlaysWithPlayback = [
    'route-view',
    'gps-speed-overlay',
    'flow-rate-overlay',
    'application-rate-gal-ac-overlay',
    'weed-it-spray-mode-overlay',
    'active-boom-overlay',
    'weed-it-sensitivity-overlay',
    'weed-it-status-overlay',
    'weed-it-speed-overlay',
    'weed-it-pressure-overlay',
    'gps-quality-overlay',
    'gps-satellites-overlay',
    'lte-rsrp-overlay',
    'lte-rsrq-overlay'
];

function getPlaybackLength(markerType, route) {
    switch (markerType) {
        case 'route-view':
        case 'gps-speed-overlay':
        case 'weed-it-spray-mode-overlay':
        case 'weed-it-sensitivity-overlay':
        case 'weed-it-margin-overlay':
        case 'weed-it-status-overlay':
        case 'weed-it-speed-overlay':
        case 'weed-it-pressure-overlay':
        case 'gps-quality-overlay':
        case 'gps-satellites-overlay':
        case 'active-boom-overlay':
        case 'flow-rate-overlay':
        case 'lte-rsrp-overlay':
        case 'application-rate-gal-ac-overlay':
        case 'lte-rsrq-overlay':
            return route.length;
        default:
            return 0;
    }
}

function Map({
    id,
    baseUrl,
    markers,
    solenoidOpenEvents,
    route,
    markerType,
    activeMarker,
    setActiveMarker,
    setMarkerType,
    isRunOver,
    isAiHeatmap,
    mapStyle,
    polygons,
    tractor
}) {
    const runDataFromRedux = useSelector(selectRouteById(id));

    const [zoom, setzoom] = useState(18);
    const [center, setCenter] = React.useState({ lat: 37.851, lng: -98.205 });
    const mapRef = React.useRef(null);
    const [map, setMap] = React.useState(null);
    const [mapTypeId, setMapTypeId] = useState(google.maps.MapTypeId.HYBRID);

    const [userLocation, setUserLocation] = useState(null);
    const [geoError, setGeoError] = useState(null);
    const [showUserLocation, setShowUserLocation] = useState(false);
    const [showBlockNames, setShowBlockNames] = useState(false);
    const [rightClickInfo, setRightClickInfo] = useState(null);
    const [legendData, setLegendData] = useState(null);
    const [isMapOptionsBoxOpen, setIsMapOptionsBoxOpen] = useState(true);

    // Playback state
    const [playIndex, setPlayIndex] = useState(0);
    const [playbackEnabled, setPlaybackEnabled] = useState(false);

    const dispatch = useDispatch();

    const handleActiveMarker = (marker) => {
        if (marker === activeMarker) return;
        setActiveMarker(marker);
    };

    const handleOnLoad = (map) => {
        try {
            setMap(map);
            const bounds = new google.maps.LatLngBounds();
            markers.forEach(({ position }) => bounds.extend(position));
        } catch (error) { }
    };

    const userLocationIcon = window.google ? {
        path:
            "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: "#1976d2",
        fillOpacity: 1,
        scale: 2,
        strokeColor: "#fff",
        strokeWeight: 2,
        labelOrigin: new window.google.maps.Point(12, -5),
        anchor: new window.google.maps.Point(12, 24)
    } : undefined;

    const saveMapDataToRedux = (hasMoved) => {
        if ((!markers || markers.length === 0) && (!route || route.length === 0))
            return;

        dispatch(
            addRoute({
                id: id,
                markerType: markerType,
                zoom: map?.zoom,
                keepCentered: hasMoved
                    ? false
                    : hasMoved === false
                        ? true
                        : (runDataFromRedux?.keepCentered ?? true),
                center: {
                    lat: map?.center?.lat(),
                    lng: map?.center?.lng()
                }
            })
        );
    };

    useEffectOnce(() => {
        if (!runDataFromRedux) return;
        setzoom(runDataFromRedux.zoom);

        if (runDataFromRedux?.keepCentered) {
            setCenter(runDataFromRedux.center);
        }
    });

    useEffect(() => {
        if (markers && markers.length > 0) {
            setzoom(runDataFromRedux?.zoom || 18);
        } else if (route && route.length > 0) {
            setzoom(runDataFromRedux?.zoom || 18);
        } else {
            setzoom(runDataFromRedux?.zoom || 3);
        }
    }, [markers, route, runDataFromRedux]);

    useEffectOnce(() => {
        if (route && route.length > 0) {
            setMarkerType('route-view');
        }
    }, [markers, route]);

    useEffect(() => {
        if (!route && !markers) return;

        if (runDataFromRedux?.keepCentered) {
            setCenter(
                route && route.length > 1
                    ? route[route.length - 1]
                    : markers && markers.length > 1
                        ? markers[markers.length - 1].position
                        : { lat: 37.851, lng: -98.205 }
            );
        } else if (runDataFromRedux && !runDataFromRedux.keepCentered) {
            setCenter(runDataFromRedux.center);
        } else {
            setCenter(route[route.length - 1]);
        }
    }, [runDataFromRedux, route, markers]);

    const debouncedSaveMapDataToRedux = debounce(() => {
        saveMapDataToRedux();
    }, 1000);

    useEffect(() => {
        if (!id) return;
        debouncedSaveMapDataToRedux();
    }, [id, markerType]);

    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoError('Geolocation is not supported by your browser');
            return;
        }
        const watchId = navigator.geolocation.watchPosition(
            (position) => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                });
            },
            (err) => {
                setGeoError('Unable to retrieve your location');
            },
            {
                enableHighAccuracy: true,
                maximumAge: 1000,
                timeout: 10000
            }
        );
        return () => {
            navigator.geolocation.clearWatch(watchId);
        };
    }, []);

    const recenterToUserLocation = () => {
        if (userLocation && map) {
            map.panTo(userLocation);
        }
    };

    const handleMapRightClick = (e) => {
        setRightClickInfo({
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
        });
    };

    const handleCopyCoords = () => {
        if (rightClickInfo) {
            const text = `${rightClickInfo.lat}, ${rightClickInfo.lng}`;
            navigator.clipboard.writeText(text);
            setRightClickInfo(null);
        }
    };

    const handleMapTypeIdChanged = () => {
        if (map) {
            setMapTypeId(map.getMapTypeId());
        }
    };

    const [drawerOpen, setDrawerOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [isLegendOpen, setIsLegendOpen] = useState(true);

    // Controls content with collapsible header
    const controls = (
        <Box
            sx={{
                minWidth: isMapOptionsBoxOpen ? 180 : 160,
                width: isMapOptionsBoxOpen ? 275 : 160,
                bgcolor: '#fff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                zIndex: 10,
                overflow: 'hidden',
                transition: 'width 0.2s, min-width 0.2s',
                position: 'relative',
                p: 0
            }}
        >
            {/* Header */}
            <Box
                onClick={() => setIsMapOptionsBoxOpen(open => !open)}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '6px 12px',
                    bgcolor: '#f5f5f5',
                    fontWeight: 400,
                    fontSize: 16,
                    borderBottom: isMapOptionsBoxOpen ? '1px solid #eee' : 'none',
                    minHeight: 40,
                    height: 40,
                    transition: 'all 0.2s',
                }}
            >
                <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{
                        fontWeight: 500,
                        fontSize: '1rem',
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        flex: 1,
                        transition: 'font-size 0.2s',
                    }}
                >
                    Map Options
                </Typography>
                {isMapOptionsBoxOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
            {/* Content */}
            {isMapOptionsBoxOpen && (
                <Box sx={{ p: 2, minWidth: 180, position: 'relative' }}>
                    {isMobile && (
                        <IconButton
                            aria-label="Close"
                            onClick={() => setDrawerOpen(false)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1
                            }}
                            size="large"
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                    <Stack spacing={1} sx={{ mt: isMobile ? 4 : 0 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={runDataFromRedux?.keepCentered}
                                    onChange={e => { toggleCenter(); }}
                                    color="primary"
                                />
                            }
                            label="Follow Sprayer"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showUserLocation}
                                    onChange={e => setShowUserLocation(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Show My Location"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={showBlockNames}
                                    onChange={e => setShowBlockNames(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Show Block Names"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                recenterToUserLocation();
                                saveMapDataToRedux(true);
                            }}
                            sx={{ width: '100%' }}
                            disabled={!userLocation}
                        >
                            Center on My Location
                        </Button>
                        <Box sx={{ mt: 2 }}>
                        <FormControl size="small" sx={{ width: '100%', mb: 1, mt:2 }}>
                            <InputLabel id="map-type-label">Map Type</InputLabel>
                            <Select
                                labelId="map-type-label"
                                value={mapTypeId}
                                label="Map Type"
                                onChange={e => setMapTypeId(e.target.value)}
                            >
                                <MenuItem value={google.maps.MapTypeId.HYBRID}>Hybrid</MenuItem>
                                <MenuItem value={google.maps.MapTypeId.SATELLITE}>Satellite</MenuItem>
                                <MenuItem value={google.maps.MapTypeId.ROADMAP}>Roadmap</MenuItem>
                                <MenuItem value={google.maps.MapTypeId.TERRAIN}>Terrain</MenuItem>
                            </Select>
                        </FormControl>
                        </Box>
                        {overlaysWithPlayback.includes(markerType) && (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={playbackEnabled}
                                            onChange={e => setPlaybackEnabled(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Enable Playback"
                                />
                                {playbackEnabled && (
                                    <PlaybackController
                                        length={getPlaybackLength(markerType, route)}
                                        onChange={setPlayIndex}
                                        initialIndex={playIndex}
                                    />
                                )}
                            </>
                        )}
                    </Stack>
                </Box>
            )}
        </Box>
    );

    function getAiHeatmapData() {
        const result = markers
            .filter((marker) => {
                return marker?.images.some(
                    (image) =>
                        image.aiDiscoveries.length > 0 &&
                        image.aiDiscoveries.some(
                            (discovery) =>
                                discovery?.toLowerCase().trim() ===
                                markerType?.toLowerCase().trim()
                        )
                );
            })
            .map(
                ({ position }) =>
                    new google.maps.LatLng(
                        parseFloat(position.lat),
                        parseFloat(position.lng)
                    )
            );
        return result;
    }

    const weedPressureHeatMap = useMemo(() => {
        var openEventLocations = solenoidOpenEvents.map((item) => {
            return {
                location: new google.maps.LatLng(item.lat, item.lng)
            };
        });
        return openEventLocations;
    }, [solenoidOpenEvents]);

    const sprayDensityHeatMap = useMemo(() => {
        return route.map((item) => ({
            location: new google.maps.LatLng(item.lat, item.lng),
            weight: item.flowRateGpm ?? 0
        }));
    }, [route]);

    const toggleCenter = () => {
        if (runDataFromRedux?.keepCentered) {
            saveMapDataToRedux(true);
        } else {
            if (route && route.length > 0) {
                setCenter(route[route.length - 1]);
            } else if (markers && markers.length > 0) {
                setCenter(markers[markers.length - 1].position);
            }
            saveMapDataToRedux(false);
        }
    };

    const handleDragEnd = () => {
        if (!map) return;
        const center = map.getCenter();
        if (!center) return;
        const newCenter = center.toJSON();
        saveMapDataToRedux(true);
    };

    const getPlaybackData = (data) =>
        playbackEnabled
            ? data.slice(0, playIndex + 1)
            : data;

    return (
        <MapLegendContext.Provider value={{ setLegend: setLegendData }}>
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <GoogleMap
            zoom={zoom}
            onZoomChanged={debouncedSaveMapDataToRedux}
            onDragEnd={handleDragEnd}
            onRightClick={handleMapRightClick}
            onClick={(e) => {
                setActiveMarker(null);
                if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            }}
            ref={mapRef}
            options={{
                disableDefaultUI: true,
                scaleControl: true,
                mapTypeId: mapTypeId
            }}
            onLoad={handleOnLoad}
            onMapTypeIdChanged={handleMapTypeIdChanged}
            center={center}
            mapContainerStyle={{ ...containerStyle, ...mapStyle }}
        >
            {polygons
                .filter((i) => i.paths)
                .map((polygon, idx) => {
                    const centroid = getPolygonCentroid(polygon.paths);
                    return (
                        <React.Fragment key={polygon.name}>
                            <Polygon
                                paths={polygon.paths}
                                options={{
                                    fillColor: generateRandomColor(polygon.name),
                                    fillOpacity: 0.5,
                                    strokeColor: 'green',
                                    strokeOpacity: .75,
                                    strokeWeight: 3
                                }}
                                onRightClick={(e) => {
                                    setRightClickInfo({
                                        lat: e.latLng.lat(),
                                        lng: e.latLng.lng(),
                                    });
                                }}
                                onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                            />
                            {showBlockNames && centroid && centroid.lat != null && centroid.lng != null && (
                                <OverlayViewF
                                    position={centroid}
                                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                >
                                    <div style={{
                                        transform: 'translate(-50%, -50%)',
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: '#222',
                                        whiteSpace: 'nowrap',
                                        pointerEvents: 'none',
                                        textShadow: '0 0 4px #fff, 0 0 4px #fff'
                                    }}>
                                        <div>{polygon.name}</div>
                                        <div>{polygon.areaAcres > 0 ? polygon.areaAcres.toFixed(1) : '-'} ac</div>
                                    </div>
                                </OverlayViewF>
                            )}
                        </React.Fragment>
                    );
                })}

            {markerType === 'marker-view' ? (
                markers
                    .filter((i) => i.images.length)
                    .map((marker, index) => (
                        <MarkerAI
                            key={index}
                            runId={id}
                            marker={marker}
                            handleActiveMarker={handleActiveMarker}
                            setActiveMarker={setActiveMarker}
                            activeMarker={activeMarker}
                            baseUrl={baseUrl}
                        />
                    ))
            ) : markerType === 'spraydensity-heatmap' ? (
                <HeatmapLayer data={sprayDensityHeatMap} />
            ) : markerType === 'application-rate-gal-ac-overlay' ? (
                <ApplicationRateOverlay routes={[getPlaybackData(route)]} tractor={tractor} />
            ) : markerType === 'flow-rate-overlay' ? (
                <FlowRateOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'gps-speed-overlay' ? (
                <GpsSpeedOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'gps-quality-overlay' ? (
                <GpsQualityOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'lte-rsrp-overlay' ? (
                <LteRSRPOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'lte-rsrq-overlay' ? (
                <LteRSRQOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'gps-satellites-overlay' ? (
                <GpsSatellitesOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'active-boom-overlay' ? (
                <ActiveBoomOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-spray-mode-overlay' ? (
                <WeedItPwmModeOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-sensitivity-overlay' ? (
                <WeedItSensitivityOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-margin-overlay' ? (
                <WeedItMarginOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-status-overlay' ? (
                <WeedItStatusOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-speed-overlay' ? (
                <WeedItSpeedOverlay routes={[getPlaybackData(route)]} />
            ) : markerType === 'weed-it-pressure-overlay' ? (
                <WeedItPressureOverlay routes={[getPlaybackData(route)]} tractor={tractor} />
            ) : markerType === 'route-view' ? (
                <RouteOverlay routes={[getPlaybackData(route)]} />
            ) : isAiHeatmap ? (
                <HeatmapLayer data={getAiHeatmapData()} />
            ) : (
                markers
                    .filter(
                        (i) =>
                            i.images.length &&
                            i.images.some(
                                (image) =>
                                    image.aiDiscoveries.length > 0 &&
                                    image.aiDiscoveries.some(
                                        (discovery) =>
                                            discovery?.toLowerCase().trim() ===
                                            markerType?.toLowerCase().trim()
                                    )
                            )
                    )
                    .map((marker, index) => (
                        <MarkerAI
                            key={index}
                            runId={id}
                            marker={marker}
                            handleActiveMarker={handleActiveMarker}
                            setActiveMarker={setActiveMarker}
                            activeMarker={activeMarker}
                            baseUrl={baseUrl}
                        />
                    ))
            )}

            {/* Always show start/end markers if route exists */}
            {route && route.length > 0 && (
                <>
                    <Marker
                        position={route[route.length - 1]}
                        label={isRunOver ? 'End' : 'Current'}
                        icon={window.google ? {
                            path: ENDPOINT_MARKER,
                            fillColor: isRunOver ? 'red' : 'blue',
                            fillOpacity: 0.9,
                            scale: 2,
                            strokeColor: isRunOver ? 'red' : 'blue',
                            strokeWeight: 2,
                            labelOrigin: new window.google.maps.Point(12, -5),
                            anchor: new window.google.maps.Point(12, 24)
                        } : undefined}
                    />
                    <Marker
                        position={route[0]}
                        label='Start'
                        icon={window.google ? {
                            path: ENDPOINT_MARKER,
                            fillColor: 'green',
                            fillOpacity: 0.9,
                            scale: 2,
                            strokeColor: 'green',
                            strokeWeight: 2,
                            labelOrigin: new window.google.maps.Point(12, -5),
                            anchor: new window.google.maps.Point(12, 24)
                        } : undefined}
                    />
                </>
            )}

            {showUserLocation && userLocation && userLocationIcon && (
                <Marker
                    position={userLocation}
                    icon={userLocationIcon}
                    title="Your Location"
                    zIndex={9999}
                />
            )}

            {rightClickInfo && (
                <InfoWindow
                    position={rightClickInfo}
                    onCloseClick={() => setRightClickInfo(null)}
                >
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 2, pb: 1, px: 1, minWidth: 120 }}>
                        <IconButton onClick={() => setRightClickInfo(null)} aria-label="Close" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5 }}>
                            <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Location</Typography>
                        <Typography sx={{ fontSize: 13 }}>Lat: {rightClickInfo.lat.toFixed(6)}</Typography>
                        <Typography sx={{ fontSize: 13 }}>Long: {rightClickInfo.lng.toFixed(6)}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <IconButton
                                onClick={handleCopyCoords}
                                aria-label="Copy coordinates"
                                title="Copy coordinates"
                            >
                                <ContentCopyIcon />
                            </IconButton>
                            {isMobile && (
                                <IconButton
                                    component="a"
                                    href={`https://maps.google.com/maps?daddr=${rightClickInfo.lat},${rightClickInfo.lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Navigate to coordinates"
                                    title="Navigate"
                                >
                                    <NavigationIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </InfoWindow>
            )}

            {playbackEnabled && route && route.length > 0 && playIndex >= 0 && playIndex < route.length && (
                <Marker
                    position={route[playIndex]}
                    label="Playback"
                    icon={window.google ? {
                        path: ENDPOINT_MARKER,
                        fillColor: 'orange',
                        fillOpacity: 0.9,
                        scale: 2,
                        strokeColor: 'orange',
                        strokeWeight: 2,
                        labelOrigin: new window.google.maps.Point(12, -5),
                        anchor: new window.google.maps.Point(12, 24)
                    } : undefined}
                    zIndex={10001}
                />
            )}

            {isMobile ? (
                <>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 10001,
                            background: '#fff',
                            boxShadow: 1,
                        }}
                        onClick={() => setDrawerOpen(true)}
                        size="large"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Drawer
                        anchor="right"
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                    >
                        {controls}
                    </Drawer>
                </>
            ) : (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 10000
                    }}
                >
                    {controls}
                </Box>
            )}

            <style>
                {`
                    .gm-ui-hover-effect {
                        display: none !important;
                    }
                `}
            </style>
        </GoogleMap>
        {legendData && (
            <Box sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10000,
                background: '#fff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                minWidth: isMobile && !isLegendOpen ? 'unset' : 160,
                width: isMobile && !isLegendOpen ? 40 : 'auto',
                overflow: 'hidden',
                pointerEvents: 'auto',
            }}>
                {/* Clickable header */}
                <Box
                    onClick={() => setIsLegendOpen(o => !o)}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isMobile && !isLegendOpen ? 'center' : 'space-between',
                        p: isMobile && !isLegendOpen ? 0 : '6px 12px',
                        bgcolor: '#f5f5f5',
                        borderBottom: isLegendOpen ? '1px solid #eee' : 'none',
                        minHeight: 40,
                        height: 40,
                        width: isMobile && !isLegendOpen ? 40 : 'auto',
                    }}
                >
                    {!(isMobile && !isLegendOpen) && (
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500, fontSize: '0.85rem', color: 'text.primary', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {legendData.legendTitle}
                        </Typography>
                    )}
                    {isLegendOpen ? <ExpandLess /> : <ExpandMore />}
                </Box>
                {/* Collapsible content */}
                {isLegendOpen && (
                    <Box sx={{ p: 2, maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
                        {legendData.ranges.map((r, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 0.5,
                                    cursor: 'pointer',
                                    background: legendData.discrete
                                        ? legendData.selectedLegendValue === r.min ? '#e3f2fd' : 'transparent'
                                        : legendData.selectedLegendValue === r ? '#e3f2fd' : 'transparent',
                                    borderRadius: 1,
                                    px: 0.5,
                                }}
                                onClick={() => legendData.setSelectedLegendValue(legendData.discrete ? r.min : r)}
                            >
                                <Box sx={{
                                    width: 24,
                                    height: 8,
                                    background: r.dashed ? 'transparent' : r.color,
                                    borderRadius: 1,
                                    mr: 1,
                                    border: r.dashed ? `3px dashed ${r.color}` : undefined,
                                    flexShrink: 0,
                                }} />
                                <Typography variant="body2">{legendData.legendFormatter(r)}</Typography>
                            </Box>
                        ))}
                        <Box sx={{ mt: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                                onClick={() => legendData.setSelectedLegendValue(null)}
                            >
                                Clear Highlight
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        )}
        </Box>
        </MapLegendContext.Provider>
    );
}

export default Map;